import { Divider, Grid2 as Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { createCompany, updateCompany } from '../../api/business/endpoints';
import type { Company } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { useT } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/sk';

type Details = {
  registrationNumber: string;
  taxNumber: string;
  vatNumber: string;
  street: string;
  city: string;
  postalCode: string;
};

const DETAIL_FIELDS: Array<{ key: keyof Details; label: MessageKey; size: number }> = [
  { key: 'registrationNumber', label: 'field.registrationNumber', size: 4 },
  { key: 'taxNumber', label: 'field.taxNumber', size: 4 },
  { key: 'vatNumber', label: 'field.vatNumber', size: 4 },
  { key: 'street', label: 'field.street', size: 12 },
  { key: 'postalCode', label: 'field.postalCode', size: 4 },
  { key: 'city', label: 'field.city', size: 8 },
];

/**
 * Creating a company is limited by the licence and grants its creator every company permission (AC-05.2).
 * Its address and numbers are what an invoice addressed to it prints (R0006 D-12).
 */
export const CompanyDialog = ({ company, onClose }: { company: Company | null; onClose: () => void }) => {
  const t = useT();
  const [name, setName] = useState(company?.name ?? '');
  const [details, setDetails] = useState<Details>({
    registrationNumber: company?.registrationNumber ?? '',
    taxNumber: company?.taxNumber ?? '',
    vatNumber: company?.vatNumber ?? '',
    street: company?.street ?? '',
    city: company?.city ?? '',
    postalCode: company?.postalCode ?? '',
  });
  const [country, setCountry] = useState(company?.country ?? 'SK');
  const [currency, setCurrency] = useState(company?.currency ?? 'EUR');

  const mutation = useAppMutation(
    () => {
      const trimmed = Object.fromEntries(
        Object.entries(details).map(([key, value]) => [key, value.trim()]),
      ) as Details;

      // Editing sends null for an emptied field, so it is cleared; creating leaves it out.
      return company
        ? updateCompany(company.id, {
            name: name.trim(),
            ...(Object.fromEntries(
              Object.entries(trimmed).map(([key, value]) => [key, value === '' ? null : value]),
            ) as Record<keyof Details, string | null>),
            country: country.trim().toUpperCase(),
          })
        : createCompany({
            name: name.trim(),
            ...(Object.fromEntries(
              Object.entries(trimmed).filter(([, value]) => value !== ''),
            ) as Partial<Details>),
            country: country.trim().toUpperCase() || undefined,
            currency: currency.trim().toUpperCase() || undefined,
          });
    },
    ['companies', 'billing', 'licence-usage', 'me'],
    onClose,
  );

  return (
    <FormDialog
      title={company ? t('companies.edit') : t('companies.create')}
      onClose={onClose}
      onSubmit={() => mutation.mutate(undefined)}
      submitLabel={company ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending}
      error={mutation.error}
    >
      <TextField
        required
        label={t('field.companyName')}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {company ? null : (
        <TextField
          label={t('field.currency')}
          helperText={t('companies.currencyHint')}
          value={currency}
          inputProps={{ maxLength: 3 }}
          onChange={(e) => setCurrency(e.target.value)}
        />
      )}
      <Divider />
      <Typography variant="subtitle2">{t('companies.billingSection')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('companies.billingHint')}
      </Typography>
      <Grid container spacing={2}>
        {DETAIL_FIELDS.map((field) => (
          <Grid key={field.key} size={{ xs: 12, sm: field.size }}>
            <TextField
              fullWidth
              label={t(field.label)}
              value={details[field.key]}
              onChange={(e) => setDetails({ ...details, [field.key]: e.target.value })}
            />
          </Grid>
        ))}
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label={t('field.country')}
            value={country}
            inputProps={{ maxLength: 2 }}
            onChange={(e) => setCountry(e.target.value)}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
};
